/**
 * The topbar's status dot lights up whenever data arrives — an API answer, a
 * board broadcast, a terminal's output — and fades over `FADE_MS`, so a glance
 * says whether anything has come over the wire lately, not only that the
 * socket is up.
 *
 * Module state, not a store: `pulse()` is called from the fetch wrapper and
 * both sockets, and must cost next to nothing — a terminal pulses on every
 * frame it draws. It restarts one Web Animation, at most every `THROTTLE_MS`.
 */
export const FADE_MS = 10_000;
const THROTTLE_MS = 250;

/** A glow as data lands, then a fade that does most of its dimming early: the
 *  tmux view polls every 3s, and an ease-in fade restarted that often never
 *  visibly moves. The dot is only faded while connected, hence the green. */
const KEYFRAMES: Keyframe[] = [
  { opacity: 1, boxShadow: "0 0 6px 3px var(--st-done)", offset: 0 },
  { opacity: 0.85, boxShadow: "0 0 0 0 transparent", offset: 0.06, easing: "cubic-bezier(0.1, 0.7, 0.3, 1)" },
  { opacity: 0.12, boxShadow: "0 0 0 0 transparent", offset: 1 },
];

let el: HTMLElement | null = null;
let anim: Animation | null = null;
let last = 0;

/** The element that fades; null when it goes away. */
export function pulseTarget(target: HTMLElement | null): void {
  anim?.cancel();
  anim = null;
  el = target;
  last = 0;
}

/** Something just came over the network. */
export function pulse(): void {
  const now = performance.now();
  if (!el || now - last < THROTTLE_MS) return;
  last = now;
  anim?.cancel();
  anim = el.animate?.(KEYFRAMES, { duration: FADE_MS, fill: "forwards" }) ?? null;
}
